"use client";

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Button, Section } from "../primitives";
import { SectionHeader } from "../shared/SectionHeader";
import { emitTrack } from "../shared/track";
import { Turnstile, TURNSTILE_RESPONSE_FIELD } from "../shared/Turnstile";
import styles from "./LeadForm.module.css";
import type { LeadFormProps } from "./LeadForm.types";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

type Status = "idle" | "sending" | "sent" | "error";

/** Lien Cal.com intégrable, pré-rempli avec le nom et l'email. */
export function bookingEmbedUrl(bookingUrl: string, name: string, email: string): string {
  const url = new URL(bookingUrl);
  url.searchParams.set("embed", "true");
  if (name) url.searchParams.set("name", name);
  if (email) url.searchParams.set("email", email);
  return url.toString();
}

/**
 * Formulaire de qualification en plusieurs écrans : une question par écran,
 * puis les coordonnées, un seul envoi à la fin, par email (notre route ou
 * Web3Forms). L'envoi réussi affiche l'agenda de réservation pré-rempli. Avec
 * `trackAs`, le bloc signale `form_start` (première réponse) et
 * `generate_lead` (envoi réussi) à la mesure d'audience du site
 * (`shared/track`), qui ne les transmet qu'après accord du visiteur. Avec
 * `turnstileSiteKey`, l'écran des coordonnées porte le widget anti-robot
 * Turnstile et son jeton part avec la demande. Par défaut aucune animation entre
 * les écrans : on passe d'une question à l'autre sans attendre. `transitions`
 * les active pour les pages où le formulaire est le seul contenu.
 */
export function LeadForm({
  badge,
  title,
  titleAs = "h2",
  intro,
  questions,
  contactTitle = "Où pouvons-nous vous répondre ?",
  submitLabel = "Envoyer",
  consent,
  success,
  bookingUrl,
  delivery,
  page,
  trackAs,
  turnstileSiteKey,
  transitions = false,
  layout = "card",
  tone = "grey",
  id,
}: LeadFormProps) {
  const full = layout === "fullscreen";
  const total = questions.length + 1;
  const [step, setStep] = useState(0);
  // Sens du dernier changement d'écran : l'animation d'entrée en dépend.
  const [dir, setDir] = useState<"forward" | "back">("forward");
  const go = (next: number): void => {
    setDir(next > step ? "forward" : "back");
    setStep(next);
  };
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const headingRef = useRef<HTMLParagraphElement>(null);
  const started = useRef(false);
  const tracked = useRef(false);
  // Un jeton Turnstile ne sert qu'une fois : après un envoi refusé, on en redemande un.
  const [turnstileReset, setTurnstileReset] = useState(0);

  // Le focus suit l'écran (lecteurs d'écran, clavier), sauf au premier affichage.
  useEffect(() => {
    if (!started.current) {
      started.current = true;
      return;
    }
    headingRef.current?.focus();
  }, [step, status]);

  const question = questions[step];
  const set = (name: string, value: string): void => {
    if (trackAs && !tracked.current) {
      tracked.current = true;
      emitTrack("form_start", { form: trackAs });
    }
    setAnswers((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const contact = Object.fromEntries([...form.entries()].map(([k, v]) => [k, String(v)]));
    if (contact.botcheck) return; // piège à robots rempli : on ignore sans rien dire
    const fields = { ...answers, ...contact };
    delete fields.botcheck;
    if (turnstileSiteKey && !fields[TURNSTILE_RESPONSE_FIELD]) {
      setError("Vérification anti-robot en cours. Réessayez dans un instant.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      if (!delivery.accessKey) throw new Error("Le formulaire n'est pas encore relié à notre boîte mail. Écrivez-nous à contact@yellowplus.io.");
      const response = await fetch(delivery.endpoint ?? WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({
          access_key: delivery.accessKey,
          subject: `${delivery.subject} : ${fields.name ?? ""}`.trim(),
          from_name: "Site YELLOW+",
          replyto: fields.email,
          Page: page,
          ...fields,
        }),
      });
      const reply = (await response.json().catch(() => null)) as { success?: boolean; message?: string } | null;
      // Le message de notre route est écrit pour le visiteur (en français) ; celui de Web3Forms non.
      if (!response.ok || !reply?.success) throw new Error((delivery.endpoint && reply?.message) || "Envoi impossible. Réessayez dans un instant.");
      if (trackAs) emitTrack("generate_lead", { form: trackAs });
      setAnswers(fields);
      setStatus("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Envoi impossible. Réessayez dans un instant.");
      setStatus("error");
      if (turnstileSiteKey) setTurnstileReset((count) => count + 1);
    }
  };

  const progress = status === "sent" ? total : step;

  return (
    <Section tone={tone} id={id} className={[styles.section, full ? styles.full : null].filter(Boolean).join(" ")}>
      {/* En plein écran, pas d'en-tête : la question est le seul titre à
          l'écran, c'est tout le principe du modèle Tally. */}
      {full ? null : <SectionHeader badge={badge} title={title} titleAs={titleAs} intro={intro} />}
      <div
        className={[styles.card, full ? styles.cardFull : null].filter(Boolean).join(" ")}
        data-lead-form="true"
        data-animated={transitions ? "" : undefined}
      >
        <div className={styles.progress} aria-hidden="true">
          <span className={styles.progressBar} style={{ width: `${(progress / total) * 100}%` }} />
        </div>

        {status === "sent" ? (
          <div className={styles.done} data-lead-done="true">
            <p ref={headingRef} tabIndex={-1} className={styles.question}>
              {success.title}
            </p>
            <p className={styles.hint}>{success.text}</p>
            {bookingUrl ? (
              <iframe
                className={styles.booking}
                src={bookingEmbedUrl(bookingUrl, answers.name ?? "", answers.email ?? "")}
                title="Réserver un créneau"
                loading="lazy"
              />
            ) : null}
          </div>
        ) : question ? (
          <div key={step} data-lead-step={step} data-dir={dir}>
            <p className={styles.counter}>
              Question {step + 1} sur {questions.length}
            </p>
            <p ref={headingRef} tabIndex={-1} className={styles.question} id={`${id ?? "lead"}-q${step}`}>
              {question.label}
            </p>
            {question.hint ? <p className={styles.hint}>{question.hint}</p> : null}
            {question.type === "choice" ? (
              <div className={styles.choices} role="group" aria-labelledby={`${id ?? "lead"}-q${step}`}>
                {question.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={styles.choice}
                    aria-pressed={answers[question.name] === option}
                    onClick={() => {
                      set(question.name, option);
                      go(step + 1);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            ) : (
              <form
                className={styles.textStep}
                onSubmit={(event) => {
                  event.preventDefault();
                  if ((answers[question.name] ?? "").trim()) go(step + 1);
                }}
              >
                <input
                  className={styles.input}
                  aria-labelledby={`${id ?? "lead"}-q${step}`}
                  value={answers[question.name] ?? ""}
                  placeholder={question.placeholder}
                  autoComplete={question.autoComplete}
                  required
                  onChange={(event) => set(question.name, event.target.value)}
                />
                <Button type="submit" arrow>
                  Continuer
                </Button>
              </form>
            )}
            {step > 0 ? (
              <button type="button" className={styles.back} onClick={() => go(step - 1)}>
                ← Retour
              </button>
            ) : null}
          </div>
        ) : (
          <form className={styles.contact} onSubmit={submit} data-lead-contact="true">
            <p ref={headingRef} tabIndex={-1} className={styles.question}>
              {contactTitle}
            </p>
            <div className={styles.fields}>
              <label className={styles.field}>
                <span className={styles.label}>Nom et prénom *</span>
                <input className={styles.input} name="name" required autoComplete="name" defaultValue={answers.name} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Cabinet</span>
                <input className={styles.input} name="Cabinet" autoComplete="organization" defaultValue={answers.Cabinet} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Email *</span>
                <input className={styles.input} name="email" type="email" required autoComplete="email" defaultValue={answers.email} />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Téléphone *</span>
                <input className={styles.input} name="Téléphone" type="tel" required autoComplete="tel" defaultValue={answers["Téléphone"]} />
              </label>
              {/* piège à robots : invisible pour un humain */}
              <input className={styles.trap} type="checkbox" name="botcheck" tabIndex={-1} aria-hidden="true" />
            </div>
            {turnstileSiteKey ? <Turnstile siteKey={turnstileSiteKey} resetKey={turnstileReset} className={styles.turnstile} /> : null}
            {status === "error" ? (
              <p className={styles.error} role="alert">
                {error}
              </p>
            ) : null}
            <div className={styles.actions}>
              <button type="button" className={styles.back} onClick={() => go(step - 1)}>
                ← Retour
              </button>
              <Button type="submit" arrow disabled={status === "sending"}>
                {status === "sending" ? "Envoi…" : submitLabel}
              </Button>
            </div>
            <p className={styles.consent}>{consent}</p>
          </form>
        )}
      </div>
    </Section>
  );
}
